import { homePathFor, productRedirect } from './utils/product';
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { loginRedirectFor } from './utils/loginPath';
import { TenantProvider } from './contexts/TenantContext';
import { StudentFeaturesProvider, useStudentFeatures, StudentFeatures } from './contexts/StudentFeaturesContext';
import { TenantModulesProvider } from './contexts/TenantModulesContext';
import { BatchModulesProvider } from './contexts/BatchModulesContext';
import { SocketProvider, useSocket } from './contexts/SocketContext';
import { Layout } from './components/layout';
import { Spinner } from './components/common';

import { captureCareerPilotAttribution } from './utils/careerPilotAttribution';
import AdminLogPanel from './components/AdminLogPanel';
import CelebrationHost from './components/Celebration/CelebrationHost';

/*
 * ── EVERY PAGE IS ITS OWN CHUNK ─────────────────────────────────────────────────────────
 *
 * There were 209 static page imports here and no code splitting at all, so webpack emitted
 * ONE bundle containing every screen in the product. A candidate sitting an exam downloaded
 * the admin console, the CRM, the content authoring tools and the lead dashboards before
 * their paper could render — 5.7 MB uncompressed, over a venue's shared wifi, at the exact
 * moment the platform was under the most load it ever sees.
 *
 * These sit BELOW every import on purpose: `import/first` is an error, not a warning, and
 * a lazy() const between two imports fails the build outright.
 */
const CareerPilotStaging = lazy(() => import('./pages/Passport/AdminStaging'));
const CareerPilotConcepts = lazy(() => import('./pages/Passport/AdminConcepts'));
const CareerPilotCoverage = lazy(() => import('./pages/Passport/AdminAssessmentCoverage'));
const CareerPilotPaperDesign = lazy(() => import('./pages/Passport/AdminPaperDesign'));
const LabTracks = lazy(() => import('./pages/LabTracks'));
const LoginPage = lazy(() => import('./pages/Login'));
const RegisterPage = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const SetupPassword = lazy(() => import('./pages/SetupPassword/SetupPassword').then(m => ({ default: m.SetupPassword })));
const ProfileCompletion = lazy(() => import('./pages/ProfileCompletion/ProfileCompletion').then(m => ({ default: m.ProfileCompletion })));
const DashboardPage = lazy(() => import('./pages/Dashboard'));
const UsersPage = lazy(() => import('./pages/Users'));
const RolesPage = lazy(() => import('./pages/Roles'));
const BatchesPage = lazy(() => import('./pages/Batches'));
const AttendancePage = lazy(() => import('./pages/Attendance'));
const MyAttendancePage = lazy(() => import('./pages/MyAttendance'));
const AttendanceReportsPage = lazy(() => import('./pages/AttendanceReports'));
const QuizReportsPage = lazy(() => import('./pages/QuizReports'));
const QuizManagementPage = lazy(() => import('./pages/QuizManagement'));
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const QuestionManagementPage = lazy(() => import('./pages/QuestionManagement'));
const QuizzesPage = lazy(() => import('./pages/Quizzes'));
const QuizTakingPage = lazy(() => import('./pages/QuizTaking'));
const QuizResultsPage = lazy(() => import('./pages/QuizResults'));
const QuizResultsAdminPage = lazy(() => import('./pages/QuizResultsAdmin'));
const QuestionBuilder = lazy(() => import('./pages/QuestionBuilder'));
const StudentProfilePage = lazy(() => import('./pages/StudentProfile'));
const AccountProfilePage = lazy(() => import('./pages/AccountProfile'));
const OAuthCallbackPage = lazy(() => import('./pages/OAuthCallback'));
const AdminContentPage = lazy(() => import('./pages/AdminContent'));
const NotFoundPage = lazy(() => import('./pages/NotFound'));
const WeeklyReportsPage = lazy(() => import('./pages/WeeklyReports'));
const StudentProfileDetail = lazy(() => import('./pages/AdminStudentProfiles/StudentProfileDetail'));
const PlacementDrivesPage = lazy(() => import('./pages/PlacementDrives'));
const PlacementProgramRegister = lazy(() => import('./pages/PlacementProgram/Register'));
const PlacementProgramPortal = lazy(() => import('./pages/PlacementProgram/Portal'));
const PlacementProgramAdmin = lazy(() => import('./pages/PlacementProgramAdmin'));
const MyPlacementInterviews = lazy(() => import('./pages/PlacementProgramAdmin/MyInterviews'));
const WhatsAppInbox = lazy(() => import('./pages/WhatsAppInbox'));
const OutperoSettings = lazy(() => import('./pages/OutperoSettings'));
const EventApis = lazy(() => import('./pages/EventApis'));
const LeadArchivePage = lazy(() => import('./pages/LeadArchive'));
const EventApiEditor = lazy(() => import('./pages/EventApis/EventEditor'));
const TenantIntegrations = lazy(() => import('./pages/TenantIntegrations'));
const TenantBranding = lazy(() => import('./pages/TenantBranding'));
const StudentDrivesPage = lazy(() => import('./pages/Drives/StudentDrives'));
const PrepPackPage = lazy(() => import('./pages/Drives/PrepPack'));
const QuestionBooksLibrary = lazy(() => import('./pages/QuestionBooks/Library'));
const QuestionBookReader = lazy(() => import('./pages/QuestionBooks/Reader'));
const QuestionBookCheatSheet = lazy(() => import('./pages/QuestionBooks/CheatSheet'));
const QuestionBooksAdmin = lazy(() => import('./pages/QuestionBooks/Admin'));
const QuestionBookEditor = lazy(() => import('./pages/QuestionBooks/Admin').then((m) => ({ default: m.BookEditor })));
const PlacementAnalyticsPage = lazy(() => import('./pages/PlacementAnalytics'));
const TenantManagementPage = lazy(() => import('./pages/TenantManagement'));
const NotificationCenterPage = lazy(() => import('./pages/NotificationCenter'));
const StudentFeeDetailsPage = lazy(() => import('./pages/StudentFeeDetails'));
const BulkUploadPage = lazy(() => import('./pages/BulkUpload'));
const RecordingDiagnostics = lazy(() => import('./pages/RecordingDiagnostics'));
const PlatformSettings = lazy(() => import('./pages/PlatformSettings'));
const WhatsAppTemplates = lazy(() => import('./pages/WhatsAppTemplates'));
const ProblemBank = lazy(() => import('./pages/ProblemBank'));
const ProblemStudio = lazy(() => import('./pages/ProblemBank/Studio'));
const ProblemSets = lazy(() => import('./pages/ProblemBank/Sets'));
const ProblemSetEditor = lazy(() => import('./pages/ProblemBank/SetEditor'));
const QuestionPools = lazy(() => import('./pages/QuestionPools'));
const QuestionPoolEditor = lazy(() => import('./pages/QuestionPools/PoolEditor'));
const ProblemBankApi = lazy(() => import('./pages/ProblemBank/ApiAccess'));
const ProblemBundles = lazy(() => import('./pages/ProblemBundles'));
const PracticePassAdmin = lazy(() => import('./pages/PracticePass'));
const EmployeeDirectory = lazy(() => import('./pages/Employees/EmployeeDirectory'));
const EmployeeProfile = lazy(() => import('./pages/Employees/EmployeeProfile'));
const EmployeeSettings = lazy(() => import('./pages/Employees/EmployeeSettings'));
const ActivityReview = lazy(() => import('./pages/Employees/ActivityReview'));
const CompanyExpenses = lazy(() => import('./pages/Expenses/CompanyExpenses'));
const EmployeePortal = lazy(() => import('./pages/Employees/portal/EmployeePortal'));
const MyPractice = lazy(() => import('./pages/PracticePass/MyPractice'));
const InterviewHubFeed = lazy(() => import('./pages/InterviewHub/Feed'));
const InterviewHubDetail = lazy(() => import('./pages/InterviewHub/Detail'));
const InterviewHubCompany = lazy(() => import('./pages/InterviewHub/Company'));
const InterviewHubShare = lazy(() => import('./pages/InterviewHub/Share'));
const InterviewHubAdmin = lazy(() => import('./pages/InterviewHub/Admin'));
const CodingMySets = lazy(() => import('./pages/CodingPractice').then((m) => ({ default: m.MySets })));
const CodingSetView = lazy(() => import('./pages/CodingPractice').then((m) => ({ default: m.SetView })));
const CodingSolve = lazy(() => import('./pages/CodingPractice/Solve'));
const PracticeLibrary = lazy(() => import('./pages/CodingPractice/Library'));
const LibraryProblemOr = lazy(() => import('./pages/CodingPractice/Library').then((m) => ({ default: m.LibraryProblemOr })));
const MyLeave = lazy(() => import('./pages/MyLeave'));
const LeaveRequests = lazy(() => import('./pages/LeaveRequests'));
const CodePlayground = lazy(() => import('./pages/CodePlayground'));
const PassportAdminConfig = lazy(() => import('./pages/Passport/AdminConfig'));
const PassportAdminStudents = lazy(() => import('./pages/Passport/AdminStudents'));
const PassportAdminTrialPlans = lazy(() => import('./pages/Passport/AdminTrialPlans'));
const PassportAdminStudentRoadmap = lazy(() => import('./pages/Passport/AdminStudentRoadmap'));
const PassportAdminStageSkills = lazy(() => import('./pages/Passport/AdminStageSkills'));
const PassportAdminCoins = lazy(() => import('./pages/Passport/AdminCoins'));
const PassportAdminXpRewards = lazy(() => import('./pages/Passport/AdminXpRewards'));
const PassportAdminFunnel = lazy(() => import('./pages/Passport/AdminFunnel'));
const PassportAdminCurriculum = lazy(() => import('./pages/Passport/AdminCurriculum'));
const PassportAdminPathwayRules = lazy(() => import('./pages/Passport/AdminPathwayRules'));
const PassportAdminCareerRoles = lazy(() => import('./pages/Passport/AdminCareerRoles'));
const PassportAdminSkillGraph = lazy(() => import('./pages/Passport/AdminSkillGraph'));
const PassportAdminRoleBlueprint = lazy(() => import('./pages/Passport/AdminRoleBlueprint'));
const PassportAdminSkillEvidence = lazy(() => import('./pages/Passport/AdminSkillEvidence'));
const PassportAdminAssessmentPreview = lazy(() => import('./pages/Passport/AdminAssessmentPreview'));
const PassportAdminAssessmentShape = lazy(() => import('./pages/Passport/AdminAssessmentShape'));
const PassportAdminQuestionDrafts = lazy(() => import('./pages/Passport/AdminQuestionDrafts'));
const PassportAdminQuestionBank = lazy(() => import('./pages/Passport/AdminQuestionBank'));
const PassportAdminRoadmapV2 = lazy(() => import('./pages/Passport/AdminRoadmapV2'));
const PassportAdminCareerTracks = lazy(() => import('./pages/Passport/AdminCareerTracks'));
const PassportAdminPathways = lazy(() => import('./pages/Passport/AdminPathways'));
const PassportAdminMissions = lazy(() => import('./pages/Passport/AdminMissions'));
const PassportHome = lazy(() => import('./pages/Passport/PassportHome'));
const PassportMaterialViewer = lazy(() => import('./pages/Passport/MaterialViewer'));
const PassportAdminMemberProfile = lazy(() => import('./pages/Passport/AdminMemberProfile'));
const PassportMemberLayout = lazy(() => import('./pages/Passport/MemberLayout'));
const PassportCareerSetup = lazy(() => import('./pages/Passport/CareerSetup'));
const PassportAdminActivity = lazy(() => import('./pages/Passport/AdminActivity'));
const PassportAdminJourney = lazy(() => import('./pages/Passport/AdminJourney'));
const PassportLearningStudio = lazy(() => import('./pages/Passport/AdminLearningStudio'));
const PassportLearningUnit = lazy(() => import('./pages/Passport/AdminLearningUnit'));
const PassportRoadmap = lazy(() => import('./pages/Passport/Roadmap'));
const PassportSkillDna = lazy(() => import('./pages/Passport/SkillDna'));
const PassportRoleReadiness = lazy(() => import('./pages/Passport/RoleReadiness'));
const PassportPlacementReadiness = lazy(() => import('./pages/Passport/PlacementReadiness'));
const PassportSkillAssessment = lazy(() => import('./pages/Passport/SkillAssessment'));
const PassportRewards = lazy(() => import('./pages/Passport/Rewards'));
const AptitudeSoon = lazy(() => import('./pages/Passport/ComingSoon').then(m => ({ default: m.AptitudeSoon })));
const ReasoningSoon = lazy(() => import('./pages/Passport/ComingSoon').then(m => ({ default: m.ReasoningSoon })));
const PassportPractice = lazy(() => import('./pages/Passport/Practice'));
const PassportPracticeItem = lazy(() => import('./pages/Passport/PracticeItem'));
const PassportInterview = lazy(() => import('./pages/Passport/Interview'));
const PassportResumeCenter = lazy(() => import('./pages/Passport/ResumeCenter'));
const PassportCoins = lazy(() => import('./pages/Passport/Coins'));
const PassportLeaderboard = lazy(() => import('./pages/Passport/Leaderboard'));
const PassportAchievements = lazy(() => import('./pages/Passport/Achievements'));
const PassportNews = lazy(() => import('./pages/Passport/News'));
const PassportIndustryConnect = lazy(() => import('./pages/Passport/IndustryConnect'));
const MemberSupportHome = lazy(() => import('./pages/Passport/MemberSupport').then(m => ({ default: m.SupportHome })));
const MemberSupportNew = lazy(() => import('./pages/Passport/MemberSupport').then(m => ({ default: m.SupportNew })));
const MemberSupportTickets = lazy(() => import('./pages/Passport/MemberSupport').then(m => ({ default: m.SupportTickets })));
const MemberSupportTicket = lazy(() => import('./pages/Passport/MemberSupport').then(m => ({ default: m.SupportTicket })));
const MemberSupportArticle = lazy(() => import('./pages/Passport/MemberSupport').then(m => ({ default: m.SupportArticle })));
const MemberSupportBrowse = lazy(() => import('./pages/Passport/MemberSupport').then(m => ({ default: m.SupportBrowse })));
const MemberSupportAssistant = lazy(() => import('./pages/Passport/MemberSupport').then(m => ({ default: m.SupportAssistant })));
const PassportCompanies = lazy(() => import('./pages/Passport/Companies'));
const PassportMockTest = lazy(() => import('./pages/Passport/MockTest'));
const PassportAdminNews = lazy(() => import('./pages/Passport/AdminNews'));
const PassportAdminCompanies = lazy(() => import('./pages/Passport/AdminCompanies'));
const PassportAdminAnalytics = lazy(() => import('./pages/Passport/AdminAnalytics'));
const PassportAdminAssessment = lazy(() => import('./pages/Passport/AdminAssessment'));
const PassportCard = lazy(() => import('./pages/Passport/Card'));
const PassportJoin = lazy(() => import('./pages/Passport/Join'));
const HackathonExam = lazy(() => import('./pages/HackathonExam'));
const HackathonExamAdmin = lazy(() => import('./pages/HackathonExamAdmin'));
const BattleList = lazy(() => import('./pages/Battles/PublicList'));
const BattleLanding = lazy(() => import('./pages/Battles/Landing'));
const BattleExam = lazy(() => import('./pages/Battles/Exam'));
const BattleLeaderboard = lazy(() => import('./pages/Battles/Leaderboard'));
const BattlesAdmin = lazy(() => import('./pages/BattlesAdmin'));
const BattleDetail = lazy(() => import('./pages/BattlesAdmin/BattleDetail'));
const HackathonsAdmin = lazy(() => import('./pages/HackathonsAdmin'));
const HackathonResume = lazy(() => import('./pages/HackathonResume'));
const HackathonDetail = lazy(() => import('./pages/HackathonsAdmin/HackathonDetail'));
const ProjectBuilder = lazy(() => import('./pages/ProjectBuilder'));
const JobTracker = lazy(() => import('./pages/JobTracker'));
const AIMentor = lazy(() => import('./pages/AIMentor'));
const ResourceLibrary = lazy(() => import('./pages/ResourceLibrary'));
const ResourceAdmin = lazy(() => import('./pages/ResourceAdmin'));
const SpeakingPractice = lazy(() => import('./pages/SpeakingPractice'));
const SpeakingAdmin = lazy(() => import('./pages/SpeakingAdmin'));
const LogicGym = lazy(() => import('./pages/LogicGym'));
const DrillsAdmin = lazy(() => import('./pages/DrillsAdmin'));
const ThinkingLab = lazy(() => import('./pages/ThinkingLab'));
const CodeVisualizer = lazy(() => import('./pages/CodeVisualizer'));
const CodeVisualizerWorkspace = lazy(() => import('./pages/CodeVisualizer/Workspace'));
const CodeVisualizerAdmin = lazy(() => import('./pages/CodeVisualizerAdmin'));
const ThinkingLabAdmin = lazy(() => import('./pages/ThinkingLabAdmin'));
const InterviewQuestionsPage = lazy(() => import('./pages/InterviewQuestions'));
const InterviewQuestionBankPage = lazy(() => import('./pages/InterviewQuestionBank'));
const InterviewTemplateList = lazy(() => import('./pages/InterviewTemplateList'));
const InterviewTemplateCreate = lazy(() => import('./pages/InterviewTemplateCreate'));
const InterviewQBManagement = lazy(() => import('./pages/InterviewQBManagement'));
const InterviewAssignment = lazy(() => import('./pages/InterviewAssignment'));
const InterviewAnalytics = lazy(() => import('./pages/InterviewAnalytics'));
const TakeStructuredInterview = lazy(() => import('./pages/TakeStructuredInterview'));
const LiveInterview = lazy(() => import('./pages/LiveInterview'));
const InterviewFeedbackReport = lazy(() => import('./pages/InterviewFeedbackReport'));
const AdminAssignmentList = lazy(() => import('./pages/assignments').then(m => ({ default: m.AdminAssignmentList })));
const AdminAssignmentForm = lazy(() => import('./pages/assignments').then(m => ({ default: m.AdminAssignmentForm })));
const AdminSubmissions = lazy(() => import('./pages/assignments').then(m => ({ default: m.AdminSubmissions })));
const StudentAssignmentList = lazy(() => import('./pages/assignments').then(m => ({ default: m.StudentAssignmentList })));
const AssignmentWorkspace = lazy(() => import('./pages/assignments').then(m => ({ default: m.AssignmentWorkspace })));
const ExamLobby = lazy(() => import('./pages/PoolExam/ExamLobby'));
const ExamRoom = lazy(() => import('./pages/PoolExam/ExamRoom'));
const ExamResults = lazy(() => import('./pages/PoolExam/ExamResults'));
const AssignmentResult = lazy(() => import('./pages/assignments').then(m => ({ default: m.AssignmentResult })));
const AssignmentReports = lazy(() => import('./pages/AssignmentReports'));
const StudentFeaturesPage = lazy(() => import('./pages/StudentFeatures'));
const ResumeBuilderPage = lazy(() => import('./pages/ResumeBuilder'));
const PublicResumeView = lazy(() => import('./pages/ResumeBuilder/PublicResumeView'));
const CareerProfilePage = lazy(() => import('./pages/CareerProfile'));
const CareerProfileAdmin = lazy(() => import('./pages/CareerProfile/Admin'));
const PlacementPartnership = lazy(() => import('./pages/PartnerPipeline'));
const FeesPage = lazy(() => import('./pages/Fees'));
const LeadsPage = lazy(() => import('./pages/Leads'));
const TeamActivity = lazy(() => import('./pages/TeamActivity'));
const LeadDetailPage = lazy(() => import('./pages/LeadDetail'));
const LeadStagesPage = lazy(() => import('./pages/LeadStages'));
const LeadFormSettingsPage = lazy(() => import('./pages/LeadFormSettings'));
const LeadManagerBoardPage = lazy(() => import('./pages/LeadManagerBoard'));
const LeadMyPerformancePage = lazy(() => import('./pages/LeadMyPerformance'));
const LeadAuditLogsPage = lazy(() => import('./pages/LeadAuditLogs'));
const LeadPrioritySettingsPage = lazy(() => import('./pages/LeadPrioritySettings'));
const QualificationSettingsPage = lazy(() => import('./pages/QualificationSettings'));
const SalesContentLibraryPage = lazy(() => import('./pages/SalesContentLibrary'));
const LeadAnalyticsPage = lazy(() => import('./pages/LeadAnalytics'));
const MeetingsPage = lazy(() => import('./pages/Meetings'));
const LeadDistributionSettingsPage = lazy(() => import('./pages/LeadDistributionSettings'));
const FollowUpCalendarPage = lazy(() => import('./pages/FollowUpCalendar'));
const SeatReservationsPage = lazy(() => import('./pages/SeatReservations'));
const LeadAgingPage = lazy(() => import('./pages/LeadAging'));
const LeadDuplicatesPage = lazy(() => import('./pages/LeadDuplicates'));
const LeadApprovalsPage = lazy(() => import('./pages/LeadApprovals'));
const LeadKanbanPage = lazy(() => import('./pages/LeadKanban'));
const AdminCodeSnippets = lazy(() => import('./pages/CodeSnippets').then(m => ({ default: m.AdminCodeSnippets })));
const StudentCodeSnippets = lazy(() => import('./pages/CodeSnippets').then(m => ({ default: m.StudentCodeSnippets })));
const GradeSubmissions = lazy(() => import('./pages/CodeSnippets').then(m => ({ default: m.GradeSubmissions })));
const CertificatePage = lazy(() => import('./pages/Certificate/CertificatePage'));
const CertificateVerify = lazy(() => import('./pages/CertificateVerify'));
const CandidateProfile = lazy(() => import('./pages/CandidateProfile'));
const CertificatesAdmin = lazy(() => import('./pages/CertificatesAdmin'));
const AiSpend = lazy(() => import('./pages/AiSpend'));
const GoogleSheetIntegrationPage = lazy(() => import('./pages/GoogleSheetIntegration'));
const LeadScoringSettingsPage = lazy(() => import('./pages/LeadScoringSettings'));
const LeadSourcesPage = lazy(() => import('./pages/LeadSources'));
const AICallConfigPage = lazy(() => import('./pages/AICallConfig'));
const AllRegistrations = lazy(() => import('./pages/PublicQuizAdmin/AllRegistrations'));
const RegistrationDetail = lazy(() => import('./pages/PublicQuizAdmin/RegistrationDetail'));
const LearningContentLibraryPage = lazy(() => import('./pages/LearningContentLibrary'));
const CreateEditContentPage = lazy(() => import('./pages/LearningContentLibrary/CreateEditContent'));
const RecordClassPage = lazy(() => import('./pages/LearningContentLibrary/RecordClass'));
const LiveClassPage = lazy(() => import('./pages/LiveClass'));
const InteractiveLessonBuilderPage = lazy(() => import('./pages/InteractiveLessonBuilder'));
const InteractiveLessonViewerPage = lazy(() => import('./pages/InteractiveLessonViewer'));
const CurriculumListPage = lazy(() => import('./pages/CurriculumBuilder'));
const CurriculumBuilderPage = lazy(() => import('./pages/CurriculumBuilder/BuilderPage'));
const EnrollmentPlansPage = lazy(() => import('./pages/EnrollmentPlans'));
const BatchOfferingsPage = lazy(() => import('./pages/BatchOfferings'));
const CohortProgressPage = lazy(() => import('./pages/CohortProgress'));
const MyTasksPage = lazy(() => import('./pages/MyTasks'));
const MyLearningPlanPage = lazy(() => import('./pages/MyLearningPlan'));
const DayViewPage = lazy(() => import('./pages/MyLearningPlan/LearningPlanPro'));
const MyJourneyPage = lazy(() => import('./pages/MyLearningPlan/Journey'));
const AdminConcernsPage = lazy(() => import('./pages/AdminConcerns'));
const AdminSupportDesk = lazy(() => import('./pages/Support/AdminSupport').then(m => ({ default: m.AdminSupportDesk })));
const AdminSupportTicketPage = lazy(() => import('./pages/Support/AdminSupport').then(m => ({ default: m.AdminSupportTicketPage })));
const AdminSupportFaqs = lazy(() => import('./pages/Support/AdminSupport').then(m => ({ default: m.AdminSupportFaqs })));
const AdminSupportDashboard = lazy(() => import('./pages/Support/AdminSupport').then(m => ({ default: m.AdminSupportDashboard })));
const AdminSupportSettings = lazy(() => import('./pages/Support/AdminSupport').then(m => ({ default: m.AdminSupportSettings })));
const AdminSupportErrors = lazy(() => import('./pages/Support/AdminSupport').then(m => ({ default: m.AdminSupportErrors })));
const AdminSupportErrorDetail = lazy(() => import('./pages/Support/AdminSupport').then(m => ({ default: m.AdminSupportErrorDetail })));
const QuizSession = lazy(() => import('./pages/QuizSession'));
const AssessmentRegister = lazy(() => import('./pages/Assessment/Register'));
const AssessmentLanding = lazy(() => import('./pages/Assessment/Landing'));
const AssessmentExam = lazy(() => import('./pages/Assessment/Exam'));
const AssessmentResult = lazy(() => import('./pages/Assessment/Result'));
const AssessmentAdmin = lazy(() => import('./pages/AssessmentAdmin'));
const AssessmentCandidates = lazy(() => import('./pages/AssessmentCandidates'));
const AdminLogs = lazy(() => import('./pages/AdminLogs'));
const ScheduledInterviewsPage = lazy(() => import('./pages/ScheduledInterviews'));
const InterviewDetailPage = lazy(() => import('./pages/ScheduledInterviews/InterviewDetail'));
const HmsClassesPage = lazy(() => import('./pages/HmsClasses'));
const HmsRoomPage = lazy(() => import('./pages/HmsClasses/Room'));
const LiveGuestJoin = lazy(() => import('./pages/HmsClasses/GuestJoin'));
const CommunicationLab = lazy(() => import('./pages/CommunicationLab'));
const CommunicationLabAdmin = lazy(() => import('./pages/CommunicationLabAdmin'));
const MyInterviewsPage = lazy(() => import('./pages/MyInterviews'));
const PassportAdminStageCurriculum = lazy(() => import('./pages/Passport/AdminStageCurriculum'));
const PassportTopic = lazy(() => import('./pages/Passport/Topic'));
const PassportConceptJourney = lazy(() => import('./pages/Passport/ConceptJourney'));
const PassportFoundationJourney = lazy(() => import('./pages/Passport/FoundationJourney'));
const PassportJourneyDay = lazy(() => import('./pages/Passport/JourneyDay'));
const PassportWelcomeDay = lazy(() => import('./pages/Passport/WelcomeDay'));
const PassportMyPlan = lazy(() => import('./pages/Passport/MyPlan'));
const PassportOrientation = lazy(() => import('./pages/Passport/Orientation'));
const PassportAdminOrientation = lazy(() => import('./pages/Passport/AdminOrientation'));
const PassportMegaCurriculum = lazy(() => import('./pages/Passport/AdminMegaCurriculum'));
const PassportContentBuilder = lazy(() => import('./pages/Passport/AdminContentBuilder'));
const FoundationJourneyPage = lazy(() => import('./pages/FoundationJourney/FoundationJourneyPage'));
const SkillMappingPage = lazy(() => import('./pages/SkillMapping/SkillMappingPage'));

// Scheduled Interview Module


interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  requiredPermissions?: string[];
}

// Map each route-level role to the key permissions that define it
// Used to grant custom role users access to routes they have permissions for
const ROLE_TO_PERMISSIONS: Record<string, string[]> = {
  'SUPER_ADMIN': ['manage_tenants', 'manage_all_users', 'manage_system_settings'],
  'TENANT_ADMIN': ['manage_tenant_users', 'manage_roles', 'manage_tenant', 'manage_tenant_settings', 'manage_leads', 'manage_marketing', 'view_leads', 'create_leads', 'edit_leads', 'delete_leads', 'assign_leads', 'export_leads', 'view_lead_analytics', 'manage_lead_stages', 'convert_leads', 'manage_interview_templates', 'assign_interviews', 'evaluate_interviews',
    // Features with a permission of their own — so a custom role ticked for one can open its page
    'manage_placement_program', 'manage_placement_partners', 'manage_placement', 'manage_whatsapp_templates', 'chat_whatsapp', 'manage_certificates',
    'view_ai_spend', 'view_activity_logs', 'manage_concerns', 'manage_career_pilot', 'view_fees', 'manage_billing', 'manage_exams',
    'manage_battles', 'view_battles', 'manage_hackathons', 'view_hackathons', 'manage_passport', 'view_passport_members', 'view_passport_funnel',
    'manage_event_apis', 'view_admin_dashboard', 'archive_leads'],
  'INSTRUCTOR': ['create_courses', 'edit_courses', 'manage_own_courses', 'create_quiz', 'create_question', 'manage_assignments', 'grade_assignments', 'manage_snippets', 'grade_snippets', 'manage_interview_templates', 'assign_interviews', 'evaluate_interviews',
    'manage_interview_hub', 'manage_practice_pass', 'manage_problem_bank', 'manage_visualizer', 'manage_learning_plans', 'manage_live_classes',
    'manage_skill_assessment', 'manage_leave_requests', 'manage_thinking_lab', 'manage_resources', 'manage_speaking', 'manage_communication_lab', 'view_communication_progress',
    'manage_passport_members', 'manage_passport_setup', 'manage_passport_pathways', 'manage_passport_career_map', 'manage_passport_assessment', 'manage_passport_content'],
  'ATTENDANCE_ADMIN': ['mark_attendance'],
  'STAFF': ['mark_attendance', 'view_attendance', 'view_reports', 'manage_tenant_users', 'create_courses', 'view_leads', 'create_leads', 'edit_leads', 'assign_leads', 'view_lead_analytics', 'export_leads', 'convert_leads'],
  'STUDENT': ['enroll_courses', 'submit_assignments', 'view_quiz', 'take_interviews', 'view_snippets', 'attempt_interviews'],
};

/**
 * Which student page belongs to which feature switch (Student Features page, "LMS students" tab).
 * A student who types the address of a page the admin has switched off is sent back to the
 * dashboard, exactly as the menu hides it.
 */
const STUDENT_PAGE_FEATURES: [string, keyof StudentFeatures][] = [
  ['/my-tasks', 'myTasks'], ['/my-leave', 'applyLeave'], ['/interview-experiences', 'interviewExperiences'], ['/question-books', 'questionBooks'],
  ['/drives', 'drives'], ['/my-practice', 'myPractice'], ['/coding-practice', 'codingPractice'], ['/visualizer', 'codeVisualizer'],
  ['/my-interviews', 'scheduledInterviews'], ['/quizzes', 'quizzes'], ['/assignments', 'assignments'], ['/coding-snippets', 'codingSnippets'],
  ['/my-attendance', 'attendance'], ['/playground', 'codePlayground'], ['/project-builder', 'projectBuilder'], ['/job-tracker', 'jobTracker'],
  ['/ai-mentor', 'aiMentor'], ['/resource-library', 'resourceLibrary'], ['/ai-communication-lab', 'aiCommunicationLab'], ['/thinking-lab', 'thinkingLab'],
  ['/resume-builder', 'resumeBuilder'], ['/career-profile', 'careerProfile'], ['/hms-classes', 'liveClasses'], ['/my-learning', 'learningPlan'],
  ['/speaking-practice', 'speakingPractice'],
];
const featureForStudentPath = (pathname: string) =>
  STUDENT_PAGE_FEATURES.find(([p]) => pathname === p || pathname.startsWith(`${p}/`))?.[1];

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles,
  requiredPermissions,
}) => {
  const { isAuthenticated, loading, user } = useAuth();
  const { pathname, search } = useLocation();
  const { isFeatureEnabled: studentFeatureOn } = useStudentFeatures();

  if (loading) return <Spinner fullScreen />;

  if (!isAuthenticated) {
    // Send CareerPilot members to the CareerPilot login, not the LMS one. This covers
    // logging out (logout() only clears state — the redirect happens here when the guard
    // re-runs) and a session expiring mid-visit. `replace` so the back button does not
    // return to a page they are no longer allowed to see. A CareerPilot page the visitor was
    // trying to open goes along as ?next=, so the login says so and returns there afterwards.
    return <Navigate to={loginRedirectFor(pathname, search)} replace />;
  }

  if (user?.role === 'STUDENT') {
    const feature = featureForStudentPath(pathname);
    if (feature && !studentFeatureOn(feature)) return <Navigate to={homePathFor(user)} replace />;
  }

  // A page that names its own permissions opens for anyone holding one of them — the same keys
  // its menu item and its API check — whatever the role is called.
  if (requiredPermissions?.length && user && (user.permissions || []).some((p) => requiredPermissions.includes(p))) {
    return <>{children}</>;
  }

  if (requiredRoles && user && !requiredRoles.includes(user.role)) {
    // If user has permissions (custom role), check if they have any permission
    // that would be associated with the required roles
    if (user.permissions && user.permissions.length > 0) {
      const impliedPermissions = requiredRoles.flatMap(r => ROLE_TO_PERMISSIONS[r] || []);
      const hasAccess = impliedPermissions.some(p => user.permissions!.includes(p));
      if (hasAccess) {
        return <>{children}</>;
      }
    }
    return <Navigate to={homePathFor(user)} />;
  }

  return <>{children}</>;
};

// Students get the student profile wizard; staff get a plain account page.
const ProfileForRole: React.FC = () => {
  const { user } = useAuth();
  return user?.role === 'STUDENT' || user?.role === 'GUEST' ? <StudentProfilePage /> : <AccountProfilePage />;
};

// Wraps student routes to check if a feature is enabled by admin
const FeatureRoute: React.FC<{ feature: keyof StudentFeatures; children: React.ReactNode }> = ({ feature, children }) => {
  const { isFeatureEnabled } = useStudentFeatures();
  const { user } = useAuth();
  
  if (user?.role === 'STUDENT' && !isFeatureEnabled(feature)) {
    return <Navigate to={homePathFor(user)} />;
  }
  return <>{children}</>;
};

// Hot lead real-time notification toast — staff/admin only, never shown to students
const HotLeadToast: React.FC = () => {
  const { onHotLeadCreated, offHotLeadCreated } = useSocket();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<Array<{ id: number; leadId: string; leadName: string; source: string }>>([]);

  const isStaff = user && user.role !== 'STUDENT';

  useEffect(() => {
    if (!isStaff) return;
    onHotLeadCreated((data: any) => {
      const id = Date.now();
      setToasts(prev => [...prev, { id, leadId: data.leadId, leadName: data.leadName, source: data.source }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 8000);
    });
    return () => offHotLeadCreated();
  }, [onHotLeadCreated, offHotLeadCreated, isStaff]);

  if (toasts.length === 0) return null;

  return (
    <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 99999, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map(t => (
        <div
          key={t.id}
          onClick={() => navigate(`/leads/${t.leadId}`)}
          style={{
            background: '#7f1d1d',
            color: '#fff',
            padding: '12px 18px',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            maxWidth: 320,
            fontSize: '0.9rem',
            lineHeight: 1.4,
            borderLeft: '4px solid #ef4444',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 2 }}>🔥 New Hot Lead!</div>
          <div>{t.leadName}</div>
          {t.source && <div style={{ opacity: 0.7, fontSize: '0.8rem' }}>Source: {t.source}</div>}
        </div>
      ))}
    </div>
  );
};

/**
 * Redirect that keeps :params, ?query and #hash.
 *
 * Three things get dropped by a naive redirect, and each one lands the member somewhere
 * that looks fine and isn't:
 *
 *  - **:params.** `<LegacyRedirect to="/careerpilot/practice/:id" />` used the pattern
 *    LITERALLY as the destination, so /passport/practice/abc123 navigated to the string
 *    "/careerpilot/practice/:id" — the address bar really did read `:id`, and the page
 *    then looked up a problem by that name and found nothing.
 *  - **?query.** /passport/practice?kind=coding landed on the practice list unfiltered:
 *    right page, wrong content.
 *  - **#hash.** Deep links to a section lost their anchor.
 *
 * Mission links stored in the database, saved bookmarks, and anything already sent by
 * email all arrive through here, so this has to keep working indefinitely.
 */
const LegacyRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { search, hash } = useLocation();
  const params = useParams();
  const target = to.replace(/:([A-Za-z0-9_]+)/g, (whole, name: string) => {
    const value = params[name];
    // Leave an unmatched token alone rather than substituting "undefined" — a visibly
    // wrong URL is easier to diagnose than one that looks plausible and 404s.
    return value === undefined ? whole : encodeURIComponent(value);
  });
  return <Navigate to={`${target}${search}${hash}`} replace />;
};

/**
 * What a route shows while its chunk is arriving.
 *
 * Every page is now its own chunk (see the lazy() imports above), so navigating to a route
 * the browser has not visited fetches a small file first. On a fast connection that is
 * imperceptible; on a phone in an exam hall it is a beat, and a beat with a spinner in it
 * reads as loading rather than as nothing happening.
 *
 * Deliberately full-height and centred: a fallback that collapses to nothing makes the page
 * jump when the chunk lands.
 */
const RouteFallback: React.FC = () => (
  <div style={{
    minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <Spinner />
  </div>
);

/** Each route fetches only what it needs, behind this one Suspense boundary. */
/** "/" goes to the product the person is in: a member to CareerPilot, everyone else to the dashboard. */
const HomeRedirect: React.FC = () => {
  const { user } = useAuth();
  return <Navigate to={homePathFor(user)} replace />;
};

const AppRoutes: React.FC = () => {
  const { user: boundaryUser, loading: boundaryLoading } = useAuth();
  const { pathname: boundaryPath } = useLocation();
  // The two products stay apart wherever a link, a bookmark or a button would have crossed them.
  const crossing = boundaryLoading ? null : productRedirect(boundaryUser, boundaryPath);
  /**
   * A second capture, for a tagged URL reached WITHOUT a page load.
   *
   * index.tsx handles the entry, which is every real campaign click. This covers the case it
   * cannot see: an in-app navigation that carries campaign parameters — a link inside the
   * product, a router push that preserves them. Idempotent and a no-op when untagged, so the
   * cost of it being unnecessary is nothing and the cost of it being missing is a lost sale's
   * provenance.
   */
  const { search } = useLocation();
  useEffect(() => { captureCareerPilotAttribution(search); }, [search]);

  if (crossing) return <Navigate to={crossing} replace />;

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/careerpilot/join" element={<PassportJoin />} />
      {/* Placement Program — the page every ad points at (public, no login). */}
      <Route path="/placement-program" element={<PlacementProgramRegister />} />
      {/* The candidate's own page — fee and interview booking, reached by their secret link. */}
      <Route path="/placement-program/me/:token" element={<PlacementProgramPortal />} />
      <Route path="/passport/join" element={<LegacyRedirect to="/careerpilot/join" />} />
      {/*
        * Sign-in and create-account are one screen now, so this renders the same page as
        * /careerpilot/join — it simply opens on the other tab. The path is kept because links,
        * bookmarks and every "Log In" button in the product already point at it.
        */}
      <Route path="/careerpilot/login" element={<PassportJoin />} />
      <Route path="/passport/login" element={<LegacyRedirect to="/careerpilot/login" />} />
      <Route path="/careerpilot/card/:slug" element={<PassportCard />} />
      {/* Card links live in recruiters' inboxes; this redirect can never be removed. */}
      <Route path="/passport/card/:slug" element={<LegacyRedirect to="/careerpilot/card/:slug" />} />
      {/* ── Hackathon exam (no auth — a team was given a code, not an account) ── */}
      <Route path="/hackathon-exam/:token" element={<HackathonExam />} />
      <Route path="/hackathon-exam/enter/:slug" element={<HackathonExam />} />
      <Route path="/hackathon-exam" element={<HackathonExam />} />

      {/* ── Public Tech Battles (no auth) ── */}
      {/* A live-class invitation link: guests join without an account */}
      <Route path="/live/:token" element={<LiveGuestJoin />} />
      <Route path="/battles" element={<BattleList />} />
      <Route path="/battles/exam/:token" element={<BattleExam />} />
      <Route path="/battles/:slug/leaderboard" element={<BattleLeaderboard />} />
      <Route path="/battles/:slug" element={<BattleLanding />} />
      {/* Institutes are created from Tenant Management (platform administrator). The old public page
          logged the creator in AS the new institute, which would now sign the super admin out. */}
      <Route path="/create-organization" element={<Navigate to="/super-admin/tenants" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/certificate/:type/:token" element={<CertificatePage />} />
      <Route path="/verify/:code" element={<CertificateVerify />} />
      <Route path="/candidate/:token" element={<CandidateProfile />} />
      <Route path="/resume/view/:token" element={<PublicResumeView />} />
      <Route path="/quiz/:token" element={<QuizSession />} />
      <Route path="/assessment/:tenantId" element={<AssessmentLanding />} />
      <Route path="/assessment/:tenantId/register" element={<AssessmentRegister />} />
      <Route path="/assessment/exam/:token" element={<AssessmentExam />} />
      <Route path="/assessment/result/:token" element={<AssessmentResult />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/assessment-admin"
        element={
          <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN', 'INSTRUCTOR', 'MANAGER']}>
            <Layout>
              <AssessmentAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/assessment-candidates"
        element={
          <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN', 'INSTRUCTOR', 'MANAGER']}>
            <Layout>
              <AssessmentCandidates />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Hackathon exam (admin) ── */}
      <Route path="/hackathons/:hackathonId/exam" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN', 'INSTRUCTOR', 'STAFF']}><Layout><HackathonExamAdmin /></Layout></ProtectedRoute>
      } />

      {/* ── Tech Battles (admin) ── */}
      <Route path="/admin/battles" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN', 'INSTRUCTOR', 'STAFF']}><Layout><BattlesAdmin /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/battles/:id" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN', 'INSTRUCTOR', 'STAFF']}><Layout><BattleDetail /></Layout></ProtectedRoute>
      } />

      {/* Public: the link in the hackathon email and WhatsApp. No auth — the registration
          code is the credential, and the page shows only the team's own details. */}
      <Route path="/hackathons/resume/:code" element={<HackathonResume />} />

      {/* ── Hackathons (public registrations, admin side) ── */}
      <Route path="/admin/hackathons" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN', 'INSTRUCTOR', 'STAFF']}><Layout><HackathonsAdmin /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/hackathons/:id" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN', 'INSTRUCTOR', 'STAFF']}><Layout><HackathonDetail /></Layout></ProtectedRoute>
      } />

      {/* ── CareerPilot (separate product) ── */}
      <Route path="/admin/passport/assessment-coverage" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_assessment']}><Layout><CareerPilotCoverage /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/concepts" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_content']}><Layout><CareerPilotConcepts /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/config" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_setup']}><Layout><PassportAdminConfig /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/careerpilot/staging" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']}><Layout><CareerPilotStaging /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/careerpilot/paper-design" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']}><Layout><CareerPilotPaperDesign /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/students" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN', 'STAFF']} requiredPermissions={['manage_passport', 'manage_passport_members', 'view_passport_members']}><Layout><PassportAdminStudents /></Layout></ProtectedRoute>
      } />
      {/* Trial plans: a few days of limited access, given to chosen students from Members. */}
      <Route path="/admin/passport/trial-plans" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN', 'STAFF']} requiredPermissions={['manage_passport', 'manage_passport_members']}><Layout><PassportAdminTrialPlans /></Layout></ProtectedRoute>
      } />
      {/* Why THIS member got THESE missions. The endpoint existed with no screen calling
          it, so the only way to answer that question was a database query. */}
      {/* One member's full picture: setup, membership, payments, Skill DNA, readiness, roadmap, activity. */}
      <Route path="/admin/passport/students/:studentId" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN', 'STAFF']}><Layout><PassportAdminMemberProfile /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/students/:studentId/roadmap" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN', 'STAFF']}><Layout><PassportAdminStudentRoadmap /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/analytics" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_members']}><Layout><PassportAdminAnalytics /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/companies" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_content']}><Layout><PassportAdminCompanies /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/news" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_setup']}><Layout><PassportAdminNews /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/xp-rewards" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_setup']}><Layout><PassportAdminXpRewards /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/coins" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_setup']}><Layout><PassportAdminCoins /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/funnel" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_members', 'view_passport_funnel']}><Layout><PassportAdminFunnel /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/curriculum" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_content']}><Layout><PassportAdminCurriculum /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/pathways" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_pathways']}><Layout><PassportAdminPathways /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/pathway-rules" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_pathways']}><Layout><PassportAdminPathwayRules /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/career-roles" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_career_map']}><Layout><PassportAdminCareerRoles /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/skills" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_career_map']}><Layout><PassportAdminSkillGraph /></Layout></ProtectedRoute>
      } />
      {/* What a student with NO chosen role is measured against. The role blueprint
          answered that for everyone else; a first-year who says "not sure" had no list. */}
      {/* The ordered journey for a concept: what a student meets first, and what follows.
          Sits beside Concept Bank rather than replacing it — the bank owns the material,
          this owns the sequence. */}
      <Route path="/admin/passport/learning-studio" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_content']}><Layout><PassportLearningStudio /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/learning-studio/:skillKey" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']}><Layout><PassportLearningUnit /></Layout></ProtectedRoute>
      } />
      {/* The mega curriculum: Learning Units, the smallest schedulable node. Sits after the
          Learning Studio in the sidebar and in the workflow, because a unit resolves its bundle
          from published content rather than holding any. */}
      <Route path="/admin/passport/mega-curriculum" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_content']}><Layout><PassportMegaCurriculum /></Layout></ProtectedRoute>
      } />
      {/* What people actually did, in order, including the visits that never became an
          account. Separate from /admin/logs, which answers "what went wrong for this
          student" and needs a student to answer it at all. */}
      <Route path="/admin/passport/journey" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_members', 'view_passport_funnel']}><Layout><PassportAdminJourney /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/activity" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_members']}><Layout><PassportAdminActivity /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/stage-skills" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_career_map']}><Layout><PassportAdminStageSkills /></Layout></ProtectedRoute>
      } />
      {/* One stage, whole: modules, topics, the skills each teaches, and the questions behind
          them. Joins what the curriculum teaches to what the stage can actually measure. */}
      <Route path="/admin/passport/stage-curriculum" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_career_map']}><Layout><PassportAdminStageCurriculum /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/role-blueprints" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_career_map']}><Layout><PassportAdminRoleBlueprint /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/skill-evidence" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_career_map']}><Layout><PassportAdminSkillEvidence /></Layout></ProtectedRoute>
      } />
      {/* AI drafts questions; a person approves them. Nothing reaches a student unreviewed. */}
      <Route path="/admin/passport/question-drafts" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_assessment']}><Layout><PassportAdminQuestionDrafts /></Layout></ProtectedRoute>
      } />
      {/* The approved bank. Drafting shows only what is still pending, so before this there
          was no screen that could reach a question once it had been approved. */}
      {/* Roadmap V2: who is on it, the daily study time, and each topic's priority. */}
      <Route path="/admin/passport/roadmap-v2" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_content']}><Layout><PassportAdminRoadmapV2 /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/career-tracks" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']}><Layout><PassportAdminCareerTracks /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/question-bank" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_assessment']}><Layout><PassportAdminQuestionBank /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/assessment-preview" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_assessment']}><Layout><PassportAdminAssessmentPreview /></Layout></ProtectedRoute>
      } />
      {/* Paper shape per stage — question count, skills covered, difficulty mix, timer. */}
      <Route path="/admin/passport/assessment-shape" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_assessment']}><Layout><PassportAdminAssessmentShape /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/assessment" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_assessment']}><Layout><PassportAdminAssessment /></Layout></ProtectedRoute>
      } />
      <Route path="/admin/passport/missions" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_pathways']}><Layout><PassportAdminMissions /></Layout></ProtectedRoute>
      } />
      {/* Student Passport surfaces — deliberately NOT the LMS Layout (separate product) */}
      {/* Layout route: MemberShell mounts ONCE here and only <Outlet/> swaps, so the
          sidebar no longer remounts (and re-fetches) on every nav click. */}
      <Route element={<ProtectedRoute><PassportMemberLayout /></ProtectedRoute>}>
        <Route path="/careerpilot" element={<PassportHome />} />
        <Route path="/passport" element={<LegacyRedirect to="/careerpilot" />} />
        {/* CareerPilot onboarding — inside the member shell, so the rail and chrome
            stay put rather than the member dropping into a separate application. */}
        <Route path="/careerpilot/setup" element={<PassportCareerSetup />} />
        {/* The legacy Career Readiness questionnaire is retired: its submit endpoint is
            gone, so the paper could be opened and never handed in. Members go to the
            skill assessment, which is the instrument everything downstream reads. */}
        <Route path="/careerpilot/assessment" element={<Navigate to="/careerpilot/skill-assessment" replace />} />
        <Route path="/passport/assessment" element={<LegacyRedirect to="/careerpilot/assessment" />} />
        <Route path="/careerpilot/roadmap" element={<PassportRoadmap />} />
        {/* One day of the ninety, worked through HERE rather than in the LMS day player.
            Registered inside this layout block deliberately: a member who presses "Start
            today's work" must keep the CareerPilot rail — members and LMS students are
            different audiences, and the day belongs to the roadmap that sent them to it. */}
        <Route path="/careerpilot/journey/day/:day" element={<PassportJourneyDay />} />
        {/* One WELCOME day (0.1 to 0.5), on the same layout as a learning day and in the same
            shell. Its own path because JourneyDay reads its day with parseInt, so "0.1" would
            arrive there as day 1 — silently the wrong day, and the reason a member kept landing
            on Day 1 when they pressed start. */}
        <Route path="/careerpilot/journey/welcome/:day" element={<PassportWelcomeDay />} />
        {/* A checkpoint's results, shown in the CareerPilot shell so a member finishing a quiz from
            their day is not dropped into the LMS. The same page as /quiz/:quizId/results/:attemptId. */}
        <Route path="/careerpilot/quiz/:quizId/results/:attemptId" element={<QuizResultsPage />} />
        {/* An assignment's result, in the CareerPilot shell: a member finishing a project from their
            roadmap day is not dropped into the LMS portal. The same page as /assignments/:id/result. */}
        <Route path="/careerpilot/assignments/:assignmentId/result" element={<AssignmentResult />} />
        {/* Interview Experiences — the same pages as the LMS, inside the member shell. */}
        <Route path="/careerpilot/question-books" element={<QuestionBooksLibrary />} />
        <Route path="/careerpilot/question-books/cheat-sheet" element={<QuestionBookCheatSheet />} />
        <Route path="/careerpilot/question-books/:slug" element={<QuestionBookReader />} />
        <Route path="/careerpilot/interview-experiences" element={<InterviewHubFeed />} />
        <Route path="/careerpilot/interview-experiences/share" element={<InterviewHubShare />} />
        <Route path="/careerpilot/interview-experiences/company/:slug" element={<InterviewHubCompany />} />
        <Route path="/careerpilot/interview-experiences/:id" element={<InterviewHubDetail />} />
        <Route path="/careerpilot/skills" element={<PassportSkillDna />} />
        <Route path="/careerpilot/readiness" element={<PassportRoleReadiness />} />
        {/* Resume readiness and interview readiness, beside the skill figure and never
            blended with it (Module 14). */}
        <Route path="/careerpilot/placement" element={<PassportPlacementReadiness />} />
        {/* The personalised skill assessment (Modules 6-7). Distinct from
            /careerpilot/assessment, which is the free career-readiness questionnaire. */}
        <Route path="/careerpilot/skill-assessment" element={<PassportSkillAssessment />} />
        {/* Progress, badges and leaderboards. Engagement only — never a capability signal. */}
        {/*
          AI Mentor, mounted INSIDE the member shell rather than linked out to /ai-mentor.
          
          The page itself is the same component either way - it depends on nothing but its own
          api module. What differs is the chrome: /ai-mentor wraps it in the main LMS Layout, so
          a CareerPilot member who opened it from the rail would land in a different application
          with a different sidebar and no way back to CareerPilot. Mounting it here keeps the
          rail, the streak and the level on screen, which is what makes it feel like part of
          CareerPilot instead of a link out of it.
          
          /ai-mentor stays exactly as it was, for everybody who reaches it from the LMS sidebar.
        */}
        <Route path="/careerpilot/mentor" element={<AIMentor />} />
        {/* Code Playground, mounted in the shell for the same reason as the mentor above:
            /playground wraps it in the LMS Layout, which would drop a CareerPilot member into a
            different application. The component is unchanged; only the chrome differs. */}
        <Route path="/careerpilot/playground" element={<CodePlayground />} />
        <Route path="/careerpilot/visualizer" element={<CodeVisualizer />} />
        <Route path="/careerpilot/visualizer/:slug" element={<CodeVisualizerWorkspace />} />
        {/* My Progress is part of My Dashboard; an old link or bookmark lands there. */}
        <Route path="/careerpilot/progress" element={<Navigate to="/careerpilot" replace />} />
        {/* Coins buy rewards; XP never does. */}
        <Route path="/careerpilot/rewards" element={<PassportRewards />} />
        {/* Announced in the rail, not built yet: each says what is coming and where to practise meanwhile. */}
        <Route path="/careerpilot/aptitude" element={<AptitudeSoon />} />
        <Route path="/careerpilot/reasoning" element={<ReasoningSoon />} />
        <Route path="/passport/roadmap" element={<LegacyRedirect to="/careerpilot/roadmap" />} />
        {/*
          Built-ins ONLY. Left on the default 'all', this listed the admin-authored bank as
          well, so every Thinking Lab problem appeared on both screens — the same problem
          under two names, which is worse than having no second screen at all.
        */}
        <Route path="/careerpilot/practice" element={<PracticeLibrary base="/careerpilot/practice" />} />
        <Route path="/careerpilot/coding" element={<CodingMySets base="/careerpilot/coding" />} />
        <Route path="/careerpilot/coding/:setId" element={<CodingSetView base="/careerpilot/coding" />} />
        <Route path="/careerpilot/coding/:setId/:problemId" element={<CodingSolve base="/careerpilot/coding" />} />
        <Route path="/passport/practice" element={<LegacyRedirect to="/careerpilot/practice" />} />
        <Route path="/careerpilot/practice/:problemId" element={<LibraryProblemOr base="/careerpilot/practice" legacy={<PassportPracticeItem />} />} />
        {/* A material an admin wrote. Materials without an external URL were dropped by the
            mission engine, so a full lesson could be authored with nowhere to open it. */}
        <Route path="/careerpilot/material/:id" element={<PassportMaterialViewer />} />
        {/* One curriculum topic — the video, the notes and the practice, in that order.
            Where a learning mission lands: an objective used to name a skill, and a skill is
            not a thing that renders, so the card had nowhere to send anybody. */}
        <Route path="/careerpilot/topic/:topicCode" element={<PassportTopic />} />
        {/* The whole course for one skill: its topics, its subtopics, and where the student is
            in it. The endpoint behind this existed with nothing calling it, so a member could
            only ever see one step at a time and never the shape of what they were learning. */}
        <Route path="/careerpilot/learn/:skillKey" element={<PassportConceptJourney />} />
        {/* The ninety days, day by day. Separate from /roadmap, which still renders the legacy
            week-grouped journey — the two will merge once the day screen lands. */}
        <Route path="/careerpilot/orientation" element={<PassportOrientation />} />
        <Route path="/careerpilot/plan" element={<PassportMyPlan />} />
        {/* The UNIT-engine Foundation journey: ninety days, one Learning Unit each. Served
            only to a student who has one, and gated by the engine flag — a member on the
            TOPIC engine sees the "not created yet" state rather than an error. */}
        <Route path="/careerpilot/journey" element={<PassportFoundationJourney />} />
        {/*
          Bank problems still open at /careerpilot/practice/:id (ids prefixed `db:`); the
          Thinking Lab itself is the daily-challenge page mounted below.
        */}
        {/*
          Communication Lab, inside the member layout so it gets the rail.
          Registered outside this block first, it rendered as a bare page with no navigation
          — PassportShell is only a metabar passthrough; the rail comes from the layout route
          above, and a member route that skips it strands the member with no way back.
        */}
        <Route path="/careerpilot/communication" element={<CommunicationLab />} />
        {/* The real Thinking Lab — today's problem, think-first gate, run/submit, progress,
            analytics and leaderboard — inside the member shell. Practice is a separate list
            (/careerpilot/practice); this is the one-problem-a-day lab, served to members from
            the CareerPilot bank in curriculum order. */}
        <Route path="/careerpilot/thinking-lab" element={<ThinkingLab variant="careerpilot" />} />
        <Route path="/passport/practice/:id" element={<LegacyRedirect to="/careerpilot/practice/:id" />} />
        <Route path="/careerpilot/interview" element={<PassportInterview />} />
        <Route path="/passport/interview" element={<LegacyRedirect to="/careerpilot/interview" />} />
        <Route path="/careerpilot/coins" element={<PassportCoins />} />
        {/* The SAME profile wizard the LMS uses, inside the CareerPilot member
            layout instead of the LMS one. One form, one studentprofiles record,
            one completion score — a second form would disagree about what a
            complete profile is. */}
        <Route path="/careerpilot/profile" element={<StudentProfilePage />} />
        <Route path="/careerpilot/leaderboard" element={<PassportLeaderboard />} />
        <Route path="/careerpilot/achievements" element={<PassportAchievements />} />
        <Route path="/careerpilot/news" element={<PassportNews />} />
        <Route path="/careerpilot/industry-connect" element={<PassportIndustryConnect />} />
        <Route path="/careerpilot/support" element={<MemberSupportHome />} />
        <Route path="/careerpilot/support/new" element={<MemberSupportNew />} />
        <Route path="/careerpilot/support/tickets" element={<MemberSupportTickets />} />
        <Route path="/careerpilot/support/tickets/:id" element={<MemberSupportTicket />} />
        <Route path="/careerpilot/support/articles/:id" element={<MemberSupportArticle />} />
        <Route path="/careerpilot/support/browse" element={<MemberSupportBrowse />} />
        <Route path="/careerpilot/support/assistant" element={<MemberSupportAssistant />} />
        <Route path="/careerpilot/companies" element={<PassportCompanies />} />
        <Route path="/careerpilot/companies/:slug" element={<PassportCompanies />} />
        <Route path="/careerpilot/mock-test/:id" element={<PassportMockTest />} />
        <Route path="/passport/coins" element={<LegacyRedirect to="/careerpilot/coins" />} />
        <Route path="/careerpilot/resume" element={<PassportResumeCenter />} />
        <Route path="/passport/resume" element={<LegacyRedirect to="/careerpilot/resume" />} />
      </Route>
      {/* The welcome every new member meets before Day 1. Outside the programme's length. */}
      <Route path="/admin/passport/orientation" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_content']}><Layout><PassportAdminOrientation /></Layout></ProtectedRoute>
      } />

      {/* The day-by-day authoring screen: curriculum on the left, the member's day on the right. */}
      <Route path="/admin/passport/content-builder" element={
        <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN']} requiredPermissions={['manage_passport', 'manage_passport_content']}><Layout><PassportContentBuilder /></Layout></ProtectedRoute>
      } />

      <Route
        path="/interview-questions/:chapterId"
        element={
          <ProtectedRoute requiredRoles={['STUDENT']}>
            <FeatureRoute feature="myCourse">
              <Layout>
                <InterviewQuestionsPage />
              </Layout>
            </FeatureRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/recording-diagnostics"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <RecordingDiagnostics />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Employees: directory, profiles, lists. Each screen opens for the permissions its API checks. */}
      <Route path="/employees" element={<ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']} requiredPermissions={['view_employees', 'manage_employees']}><Layout><EmployeeDirectory /></Layout></ProtectedRoute>} />
      <Route path="/employees/new" element={<ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']} requiredPermissions={['manage_employees']}><Layout><EmployeeProfile /></Layout></ProtectedRoute>} />
      <Route path="/employees/:id" element={<ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']} requiredPermissions={['view_employees', 'manage_employees']}><Layout><EmployeeProfile /></Layout></ProtectedRoute>} />
      <Route path="/employee-settings" element={<ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']} requiredPermissions={['manage_employees']}><Layout><EmployeeSettings /></Layout></ProtectedRoute>} />
      {/* Review and expenses also open for managers (the server limits them to their own team), so no role gate here. */}
      <Route path="/employee-activity" element={<ProtectedRoute><Layout><ActivityReview /></Layout></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><Layout><CompanyExpenses /></Layout></ProtectedRoute>} />
      {/* The old address of the employee claims screen now lives inside Expenses. */}
      <Route path="/employee-expenses" element={<Navigate to="/expenses?tab=claims" replace />} />
      {/* The employee's own portal: open to any signed-in login, answered by the server with who they are. */}
      <Route path="/employee" element={<ProtectedRoute><Layout><EmployeePortal /></Layout></ProtectedRoute>} />

      <Route
        path="/practice-pass"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <PracticePassAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/interview-experiences"
        element={
          <ProtectedRoute>
            <Layout>
              <InterviewHubFeed />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/interview-experiences/share"
        element={
          <ProtectedRoute>
            <Layout>
              <InterviewHubShare />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/interview-experiences/company/:slug"
        element={
          <ProtectedRoute>
            <Layout>
              <InterviewHubCompany />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/interview-experiences/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <InterviewHubDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/interview-experiences"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <InterviewHubAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-practice"
        element={
          <ProtectedRoute requiredRoles={['STUDENT']}>
            <Layout>
              <MyPractice />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/problem-bundles"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <ProblemBundles />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/problem-bank/api"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <ProblemBankApi />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/problem-bank/sets"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <ProblemSets />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/problem-bank/sets/:id"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <ProblemSetEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coding-practice"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <CodingMySets base="/coding-practice" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coding-practice/:setId"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <CodingSetView base="/coding-practice" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coding-practice/:setId/:problemId"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <CodingSolve base="/coding-practice" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/problem-bank"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <ProblemBank />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/problem-bank/new"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <ProblemStudio />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/problem-bank/:id"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <ProblemStudio />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/whatsapp-templates"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <WhatsAppTemplates />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/platform-settings"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN']}>
            <Layout>
              <PlatformSettings />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-leave"
        element={
          <ProtectedRoute requiredRoles={['STUDENT']}>
            <Layout>
              <MyLeave />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/playground"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <CodePlayground />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/project-builder"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <ProjectBuilder />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/job-tracker"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <JobTracker />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ai-mentor"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <AIMentor />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/resource-library"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <ResourceLibrary />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/resources"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <ResourceAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/speaking-practice"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <SpeakingPractice />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/speaking-tasks"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <SpeakingAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/logic-gym"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <LogicGym />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/problem-solving"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <DrillsAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/thinking-lab"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <ThinkingLab />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/hms-classes"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <HmsClassesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-communication-lab"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <CommunicationLab />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/communication-lab"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']} requiredPermissions={['manage_communication_lab', 'view_communication_progress']}>
            <Layout>
              <CommunicationLabAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hms-classes/:id/room"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <HmsRoomPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/visualizer"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <CodeVisualizer />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visualizer/:slug"
        element={
          <ProtectedRoute requiredRoles={['STUDENT', 'SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <CodeVisualizerWorkspace />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/visualizer"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <CodeVisualizerAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/thinking-lab"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <ThinkingLabAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/certificates"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <CertificatesAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/ai-spend"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <AiSpend />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/leave-requests"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <LeaveRequests />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/users"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <UsersPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/fees"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF']} requiredPermissions={['manage_billing']}>
            <Layout>
              <FeesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/bulk-upload"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <BulkUploadPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/roles"
        element={
          <ProtectedRoute>
            <Layout>
              <RolesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/batches"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']} requiredPermissions={['manage_tenant_courses']}>
            <Layout>
              <BatchesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/attendance"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'ATTENDANCE_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <AttendancePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-attendance"
        element={
          <ProtectedRoute>
            <FeatureRoute feature="attendance">
              <Layout>
                <MyAttendancePage />
              </Layout>
            </FeatureRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/attendance-reports"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'ATTENDANCE_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <AttendanceReportsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Student Reports merged into the unified Student Detail page (reached from Users) */}
      <Route path="/student-reports" element={<Navigate to="/users" replace />} />

      <Route path="/admin/college/departments" element={<Navigate to="/dashboard" replace />} />

      <Route path="/admin/college/settings" element={<Navigate to="/admin/branding" replace />} />

      <Route path="/admin/college/members" element={<Navigate to="/dashboard" replace />} />

      <Route path="/admin/college/placement" element={<Navigate to="/drives/manage" replace />} />
      {/* Drives — placement drives + interview experiences; replaces the retired College area */}
      <Route
        path="/drives"
        element={
          <ProtectedRoute>
            <Layout>
              <StudentDrivesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drives/:driveId/prep"
        element={
          <ProtectedRoute>
            <Layout>
              <PrepPackPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/question-books"
        element={
          <ProtectedRoute>
            <Layout>
              <QuestionBooksLibrary />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/question-books/cheat-sheet"
        element={
          <ProtectedRoute>
            <Layout>
              <QuestionBookCheatSheet />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/question-books/:slug"
        element={
          <ProtectedRoute>
            <Layout>
              <QuestionBookReader />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/question-books"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <QuestionBooksAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/question-books/:id"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <QuestionBookEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/placement-program/my-interviews"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF', 'INSTRUCTOR', 'PLACEMENT_OFFICER']}>
            <Layout>
              <MyPlacementInterviews />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/branding"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <TenantBranding />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* The old college settings page wrote the raw tenant record and always 403'd for a college admin. */}
      <Route
        path="/admin/integrations"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <TenantIntegrations />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/event-apis"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <EventApis />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/event-apis/new"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <EventApiEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/event-apis/:id/edit"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <EventApiEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/outpero"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF']}>
            <Layout>
              <OutperoSettings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/whatsapp-inbox"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <WhatsAppInbox />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/placement-program"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF', 'PLACEMENT_OFFICER']}>
            <Layout>
              <PlacementProgramAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drives/manage"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF', 'PLACEMENT_OFFICER']}>
            <Layout>
              <PlacementDrivesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drives/analytics"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF', 'PLACEMENT_OFFICER']}>
            <Layout>
              <PlacementAnalyticsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route path="/admin/college/placement-analytics" element={<Navigate to="/drives/analytics" replace />} />

      <Route path="/admin/college/alumni" element={<Navigate to="/dashboard" replace />} />

      <Route path="/admin/college/curriculum" element={<Navigate to="/dashboard" replace />} />

      <Route path="/admin/college/crt" element={<Navigate to="/dashboard" replace />} />

      <Route
        path="/super-admin/tenants"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN']}>
            <Layout>
              <TenantManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route path="/student/college" element={<Navigate to="/drives" replace />} />

      <Route
        path="/student/fee-details"
        element={
          <ProtectedRoute>
            <FeatureRoute feature="feeDetails">
              <Layout>
                <StudentFeeDetailsPage />
              </Layout>
            </FeatureRoute>
          </ProtectedRoute>
        }
      />

      <Route path="/student/my-applications" element={<Navigate to="/drives?tab=applications" replace />} />

      <Route path="/student/alumni-directory" element={<Navigate to="/dashboard" replace />} />

      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Layout>
              <NotificationCenterPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route path="/admin/college/reports" element={<Navigate to="/dashboard" replace />} />

      {/* Student Profiles list merged into Users; keep the list route as a redirect */}
      <Route path="/admin/student-profiles" element={<Navigate to="/users" replace />} />

      {/* Unified Student Detail (reached by clicking a student in Users) */}
      <Route
        path="/lab-tracks"
        element={<ProtectedRoute><Layout><LabTracks /></Layout></ProtectedRoute>}
      />
      <Route
        path="/users/:userId"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <StudentProfileDetail />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Back-compat: old detail URL still works */}
      <Route
        path="/admin/student-profiles/:userId"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR', 'STAFF']}>
            <Layout>
              <StudentProfileDetail />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/quiz-management"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <QuizManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/quiz-results"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <QuizResultsAdminPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/interview-question-bank"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']} requiredPermissions={['manage_interviews']}>
            <Layout>
              <InterviewQuestionBankPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/question-bank"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <QuestionManagementPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/quiz/:quizId/questions"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <QuestionBuilder />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/quizzes"
        element={
          <ProtectedRoute>
            <FeatureRoute feature="quizzes">
              <Layout>
                <QuizzesPage />
              </Layout>
            </FeatureRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/quiz/:quizId/take"
        element={
          <ProtectedRoute>
            <FeatureRoute feature="quizzes">
              <QuizTakingPage />
            </FeatureRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/quiz/:quizId/results/:attemptId"
        element={
          <ProtectedRoute>
            <FeatureRoute feature="quizzes">
              <Layout>
                <QuizResultsPage />
              </Layout>
            </FeatureRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/quiz-reports"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']} requiredPermissions={['view_reports']}>
            <Layout>
              <QuizReportsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/weekly-reports"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <WeeklyReportsPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <ProfileForRole />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* OAuth Callback - handles redirects from GitHub/LinkedIn */}
      <Route path="/profile/oauth-callback" element={<OAuthCallbackPage />} />

      <Route
        path="/admin/content"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <AdminContentPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/student-features"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <StudentFeaturesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Onboarding Routes */}
      <Route path="/setup-password" element={<SetupPassword />} />
      
      <Route
        path="/complete-profile"
        element={
          <ProtectedRoute>
            <ProfileCompletion />
          </ProtectedRoute>
        }
      />

      {/* Assignment Routes - Admin */}
      <Route
        path="/admin/assignments"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <AdminAssignmentList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/assignments/create"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <AdminAssignmentForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/assignments/:id/edit"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <AdminAssignmentForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/assignments/:assignmentId/submissions"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <AdminSubmissions />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/assignments/reports"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <AssignmentReports />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Question pools: reusable shortlists of bank questions that pool exams draw from. */}
      <Route
        path="/admin/assignments/:id/exam-results"
        element={<ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}><Layout><ExamResults /></Layout></ProtectedRoute>}
      />
      <Route
        path="/admin/assignments/pools"
        element={<ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}><Layout><QuestionPools /></Layout></ProtectedRoute>}
      />
      <Route
        path="/admin/assignments/pools/:id"
        element={<ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}><Layout><QuestionPoolEditor /></Layout></ProtectedRoute>}
      />

      {/* Assignment Routes - Student */}
      <Route
        path="/assignments"
        element={
          <ProtectedRoute>
            <FeatureRoute feature="assignments">
              <Layout>
                <StudentAssignmentList />
              </Layout>
            </FeatureRoute>
          </ProtectedRoute>
        }
      />
      {/* The workspace, opened from a CareerPilot roadmap day. Full screen like the LMS one, but not
          behind the LMS "assignments" student feature (which would send a member to the LMS
          dashboard), and its exits lead back to the day. */}
      <Route
        path="/careerpilot/assignments/:assignmentId/workspace"
        element={<ProtectedRoute><AssignmentWorkspace /></ProtectedRoute>}
      />
      {/* Pool exams: the lobby inside the app, the exam room full screen. */}
      <Route
        path="/exams/:assignmentId"
        element={<ProtectedRoute><FeatureRoute feature="assignments"><Layout><ExamLobby /></Layout></FeatureRoute></ProtectedRoute>}
      />
      <Route
        path="/exams/:assignmentId/attempt/:attemptId"
        element={<ProtectedRoute><FeatureRoute feature="assignments"><ExamRoom /></FeatureRoute></ProtectedRoute>}
      />
      <Route
        path="/assignments/:assignmentId/workspace"
        element={
          <ProtectedRoute>
            <FeatureRoute feature="assignments">
              <AssignmentWorkspace />
            </FeatureRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assignments/:assignmentId/result"
        element={
          <ProtectedRoute>
            <FeatureRoute feature="assignments">
              <Layout>
                <AssignmentResult />
              </Layout>
            </FeatureRoute>
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<HomeRedirect />} />

      {/* ── Resume Builder (Student) ─── */}
      <Route
        path="/resume-builder"
        element={
          <ProtectedRoute>
            <Layout>
              <ResumeBuilderPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Career Profile Builder ─── */}
      <Route
        path="/career-profile"
        element={
          <ProtectedRoute>
            <Layout>
              <CareerProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/career-profiles"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <CareerProfileAdmin />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Placement Partnership (Admin) ─── */}
      <Route
        path="/admin/placement-partnership"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF']}>
            <Layout>
              <PlacementPartnership />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Structured Interview Module (Admin/Instructor) ─── */}
      <Route
        path="/admin/interviews/templates"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <InterviewTemplateList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/interviews/templates/create"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <InterviewTemplateCreate />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/interviews/templates/:templateId/edit"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <InterviewTemplateCreate />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/interviews/question-bank"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <InterviewQBManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/interviews/assignments"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <InterviewAssignment />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/interviews/analytics"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <InterviewAnalytics />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/interviews/report/:attemptId"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <InterviewFeedbackReport />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Structured Interview Module (Student) ──────────── */}
      {/* Unified hub: AI virtual + mock interviews now both live in MyInterviews. */}
      <Route
        path="/student/interviews"
        element={
          <ProtectedRoute>
            <Layout>
              <MyInterviewsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/interviews/take/:templateId"
        element={
          <ProtectedRoute>
            <Layout>
              <TakeStructuredInterview />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Live conversational AI interview — full-screen, no sidebar */}
      <Route
        path="/student/interviews/live/:templateId"
        element={
          <ProtectedRoute>
            <LiveInterview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/interviews/report/:attemptId"
        element={
          <ProtectedRoute>
            <Layout>
              <InterviewFeedbackReport />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Scheduled Interviews (Admin/Instructor) ── */}
      <Route
        path="/scheduled-interviews"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <ScheduledInterviewsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scheduled-interviews/:id"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <InterviewDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* ── My Interviews (Student) ── */}
      <Route
        path="/my-interviews"
        element={
          <ProtectedRoute>
            <FeatureRoute feature="scheduledInterviews">
              <Layout>
                <MyInterviewsPage />
              </Layout>
            </FeatureRoute>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<NotFoundPage />} />

      {/* Lead Management */}
      <Route
        path="/leads"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF']}>
            <Layout>
              <LeadsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-activity"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']} requiredPermissions={['manage_leads', 'view_lead_analytics']}>
            <Layout>
              <TeamActivity />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:leadId"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF']}>
            <Layout>
              <LeadDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-stages"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <LeadStagesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-form-settings"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <LeadFormSettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-manager-board"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF']}>
            <Layout>
              <LeadManagerBoardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-my-performance"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF']}>
            <Layout>
              <LeadMyPerformancePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-audit-logs"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <LeadAuditLogsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-priority-settings"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <LeadPrioritySettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/google-sheet-integration"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <GoogleSheetIntegrationPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-sources"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <LeadSourcesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-scoring-settings"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <LeadScoringSettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-distribution-settings"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <LeadDistributionSettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-call-config"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <AICallConfigPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/qualification-settings"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <QualificationSettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-content"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <SalesContentLibraryPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/archive"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']} requiredPermissions={['archive_leads']}>
            <Layout>
              <LeadArchivePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/analytics"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <LeadAnalyticsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/meetings"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF']}>
            <Layout>
              <MeetingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/follow-ups"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF']}>
            <Layout>
              <FollowUpCalendarPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seat-reservations"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF']}>
            <Layout>
              <SeatReservationsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-aging"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF']}>
            <Layout>
              <LeadAgingPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-duplicates"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <LeadDuplicatesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-approvals"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN']}>
            <Layout>
              <LeadApprovalsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-kanban"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF']}>
            <Layout>
              <LeadKanbanPage />
            </Layout>
          </ProtectedRoute>
        }
      />


      {/* Code Snippet Assessment Routes */}
      <Route
        path="/admin/coding-snippets"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <AdminCodeSnippets />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/coding-snippets/:id/submissions"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <GradeSubmissions />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/coding-snippets/grade"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout>
              <GradeSubmissions />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coding-snippets"
        element={
          <ProtectedRoute requiredRoles={['STUDENT']}>
            <FeatureRoute feature="codingSnippets">
              <Layout>
                <StudentCodeSnippets />
              </Layout>
            </FeatureRoute>
          </ProtectedRoute>
        }
      />



      {/* Website Registrations */}
      <Route
        path="/registrations"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><AllRegistrations /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/registrations/:subId"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><RegistrationDetail /></Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Learning Content Library ── */}
      <Route
        path="/learning-library"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><LearningContentLibraryPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning-library/create"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><CreateEditContentPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning-library/record"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><RecordClassPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/live-class"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR', 'STUDENT']}>
            <Layout><LiveClassPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning-library/edit/:id"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><CreateEditContentPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Interactive Lesson Builder (Admin) ── */}
      <Route
        path="/interactive-lessons/new"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><InteractiveLessonBuilderPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/interactive-lessons/edit/:id"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><InteractiveLessonBuilderPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Interactive Lesson Viewer (Student) ── */}
      <Route
        path="/interactive-lesson/play/:id"
        element={
          <ProtectedRoute>
            <InteractiveLessonViewerPage />
          </ProtectedRoute>
        }
      />

      {/* ── Curriculum Builder ── */}
      <Route
        path="/curriculum-builder"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><CurriculumListPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/curriculum-builder/create"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><CurriculumBuilderPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/curriculum-builder/:id"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><CurriculumBuilderPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Enrollment Plans (admin) ── */}
      <Route
        path="/enrollment-plans"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><EnrollmentPlansPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Batch Offerings (admin) ── */}
      <Route
        path="/batch-offerings"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><BatchOfferingsPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/batch-offerings/:id/progress"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'INSTRUCTOR']}>
            <Layout><CohortProgressPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* ── My Tasks (student unified feed) ── */}
      <Route
        path="/my-tasks"
        element={
          <ProtectedRoute requiredRoles={['STUDENT']}>
            <Layout><MyTasksPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* ── My Learning Plan (student) ── */}
      <Route
        path="/my-learning"
        element={
          <ProtectedRoute>
            <Layout><MyLearningPlanPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-learning/:enrollmentId/journey"
        element={
          <ProtectedRoute>
            <Layout><MyJourneyPage /></Layout>
          </ProtectedRoute>
        }
      />
      {/* The adaptive plan. Keyed by curriculum rather than enrollment: the plan belongs to
          the student and the curriculum, and survives an enrollment being recreated. */}
      <Route
        path="/my-learning/foundation/:curriculumId"
        element={
          <ProtectedRoute>
            <Layout><FoundationJourneyPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Authoring the bridge: which topics teach which skills. Staff only — the server
          enforces the permission, this only decides who is shown the door. */}
      <Route
        path="/admin/curricula/:curriculumId/skills"
        element={
          <ProtectedRoute requiredRoles={['TENANT_ADMIN', 'SUPER_ADMIN', 'INSTRUCTOR']}>
            <Layout><SkillMappingPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/concerns"
        element={
          <ProtectedRoute>
            <Layout><AdminConcernsPage /></Layout>
          </ProtectedRoute>
        }
      />
      {/* Help & Support desk. The server decides who sees which tickets (routes/supportRoutes.ts). */}
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF', 'INSTRUCTOR']}>
            <Layout><AdminSupportDesk /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support/dashboard"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF', 'INSTRUCTOR']}>
            <Layout><AdminSupportDashboard /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support/errors"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF', 'INSTRUCTOR']}>
            <Layout><AdminSupportErrors /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support/errors/:id"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF', 'INSTRUCTOR']}>
            <Layout><AdminSupportErrorDetail /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support/settings"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF', 'INSTRUCTOR']}>
            <Layout><AdminSupportSettings /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support/faqs"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF', 'INSTRUCTOR']}>
            <Layout><AdminSupportFaqs /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support/tickets/:id"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF', 'INSTRUCTOR']}>
            <Layout><AdminSupportTicketPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-learning/:enrollmentId/day/:day"
        element={
          <ProtectedRoute>
            <Layout><DayViewPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Admin Logs ── */}
      <Route
        path="/admin/logs"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF']}>
            <Layout><AdminLogs /></Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
    </Suspense>
  );
};

// Show the log panel only for admin / staff users
const ADMIN_ROLES = ['SUPER_ADMIN', 'TENANT_ADMIN', 'STAFF', 'INSTRUCTOR'];
const AdminLogGate: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated || !user || !ADMIN_ROLES.includes(user.role)) return null;
  return <AdminLogPanel />;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <TenantProvider>
        <StudentFeaturesProvider>
          <TenantModulesProvider>
            <BatchModulesProvider>
            <SocketProvider>
              <BrowserRouter>
                <HotLeadToast />
                <AppRoutes />
                <AdminLogGate />
                <CelebrationHost />
              </BrowserRouter>
            </SocketProvider>
            </BatchModulesProvider>
          </TenantModulesProvider>
        </StudentFeaturesProvider>
      </TenantProvider>
    </AuthProvider>
  );
};

export default App;